import {
  CLIENT_DOCUMENT_CATEGORY_LABELS,
  type ClientDocumentCategory,
  FILE_DESCRIPTION_MAX,
  type KnowledgeProjectRef,
} from '@fos/shared/contracts/knowledge';
import { FileText, X } from 'lucide-react';
import { type FormEvent, useState } from 'react';
import { errorMessage } from '../../../../lib/api';
import { uploadFile } from '../../../../lib/files-api';
import { plural } from '../../../../lib/format';
import { Button, IconButton } from '../../../../ui/Button';
import { Dialog } from '../../../../ui/Dialog';
import { Field } from '../../../../ui/Field';
import { Input } from '../../../../ui/Input';
import { Select } from '../../../../ui/Select';
import { toast } from '../../../../ui/Toast';
import { FULLSCREEN_ON_PHONE, TOUCH_BUTTON } from '../../../mobile';
import { formatFileSize, guessCategory } from './documents-format';

const NO_PROJECT = 'none';

/**
 * „Dodaj plik” do dokumentów klienta (DECISIONS 2026-10-17 „Dokumenty klienta”): wybrane albo upuszczone pliki,
 * rodzaj (podpowiedź po nazwie; bez „Widzi stawki i kwoty” bez Umowy i Oferty), opcjonalny projekt (przy dostępie tylko
 * przez projekt — wymagany) i krótki opis. Wysyła po kolei przez REST `POST /api/files`; nieudane pliki zostają w oknie
 * z komunikatem, udane znikają.
 */
export function UploadDocumentsDialog({
  open,
  onOpenChange,
  files: initialFiles,
  clientId,
  categories,
  canUploadToClient,
  uploadProjects,
  defaultProjectId,
  onUploaded,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  files: File[];
  clientId: string;
  categories: ClientDocumentCategory[];
  canUploadToClient: boolean;
  uploadProjects: KnowledgeProjectRef[];
  defaultProjectId?: string | null;
  onUploaded: () => void;
}) {
  // stan z chwili otwarcia: widok montuje okno z nowym `key` przy każdym wyborze plików (bez resetu po odświeżeniu listy)
  const [entries, setEntries] = useState<{ key: number; file: File; error?: string }[]>(() =>
    initialFiles.map((file, key) => ({ key, file })),
  );
  const files = entries.map((e) => e.file);
  const [category, setCategory] = useState<ClientDocumentCategory>(() =>
    guessCategory(
      initialFiles.map((f) => f.name),
      categories,
    ),
  );
  const [projectId, setProjectId] = useState<string>(() => {
    if (defaultProjectId && uploadProjects.some((p) => p.id === defaultProjectId)) return defaultProjectId;
    return canUploadToClient ? NO_PROJECT : (uploadProjects[0]?.id ?? NO_PROJECT);
  });
  const [description, setDescription] = useState('');
  const [projectError, setProjectError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const projectOptions = [
    ...(canUploadToClient ? [{ value: NO_PROJECT, label: 'Bez projektu (cały klient)' }] : []),
    ...uploadProjects.map((p) => ({ value: p.id, label: p.name })),
  ];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (files.length === 0 || busy) return;
    if (projectId === NO_PROJECT && !canUploadToClient) {
      setProjectError('Wybierz projekt.');
      return;
    }
    setBusy(true);
    const failed: { file: File; message: string }[] = [];
    let done = 0;
    for (const file of files) {
      try {
        await uploadFile(file, {
          ownerType: 'client',
          ownerId: clientId,
          projectId: projectId === NO_PROJECT ? null : projectId,
          category,
          description: description.trim() || null,
        });
        done += 1;
      } catch (err) {
        failed.push({ file, message: errorMessage(err) });
      }
    }
    setBusy(false);
    if (done > 0) {
      toast.success(done === 1 ? 'Plik dodany' : `Dodano ${done} ${plural(done, ['plik', 'pliki', 'plików'])}`, {
        description: done === 1 ? files.find((f) => !failed.some((x) => x.file === f))?.name : undefined,
      });
      onUploaded();
    }
    if (failed.length === 0) {
      onOpenChange(false);
      return;
    }
    setEntries((prev) =>
      prev.flatMap((e) => {
        const fail = failed.find((f) => f.file === e.file);
        return fail ? [{ ...e, error: fail.message }] : [];
      }),
    );
  };

  const count = files.length;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => !busy && onOpenChange(next)}
      title={count > 1 ? `Dodaj ${count} ${plural(count, ['plik', 'pliki', 'plików'])}` : 'Dodaj plik'}
      description="Plik trafi do dokumentów klienta."
      className={FULLSCREEN_ON_PHONE}
      footer={
        <>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy} className={TOUCH_BUTTON}>
            Anuluj
          </Button>
          <Button
            variant="primary"
            type="submit"
            form="upload-documents-form"
            loading={busy}
            disabled={count === 0}
            className={TOUCH_BUTTON}
          >
            {count > 1 ? `Wgraj ${count} ${plural(count, ['plik', 'pliki', 'plików'])}` : 'Wgraj'}
          </Button>
        </>
      }
    >
      <form id="upload-documents-form" onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <ul aria-label="Wybrane pliki" className="flex flex-col gap-2">
          {entries.map(({ key, file, error }) => (
            <li key={key} className="flex flex-col gap-1 rounded-md bg-surface px-3 py-2">
              <div className="flex items-center gap-2">
                <FileText size={16} strokeWidth={1.75} aria-hidden className="shrink-0 text-fg-muted" />
                <span className="min-w-0 flex-1 truncate text-body-s text-fg">{file.name}</span>
                <span className="shrink-0 text-caption text-fg-muted">{formatFileSize(file.size)}</span>
                {count > 1 && !busy && (
                  <IconButton
                    icon={X}
                    label={`Usuń z listy ${file.name}`}
                    size={28}
                    iconSize={14}
                    onClick={() => {
                      setEntries((prev) => prev.filter((e) => e.key !== key));
                    }}
                    className="max-md:size-11"
                  />
                )}
              </div>
              {error && (
                <p role="alert" className="text-caption text-danger">
                  {error}
                </p>
              )}
            </li>
          ))}
        </ul>
        <Field label="Rodzaj">
          <Select<ClientDocumentCategory>
            value={category}
            onValueChange={setCategory}
            options={categories.map((c) => ({ value: c, label: CLIENT_DOCUMENT_CATEGORY_LABELS[c] }))}
          />
        </Field>
        {projectOptions.length > 0 && (
          <Field
            label="Projekt"
            error={projectError}
            hint={canUploadToClient ? 'Opcjonalnie. Plik projektu zobaczy też zespół tego projektu.' : undefined}
          >
            <Select
              value={projectId}
              onValueChange={(v) => {
                setProjectId(v);
                setProjectError(null);
              }}
              options={projectOptions}
              placeholder="Wybierz projekt"
            />
          </Field>
        )}
        <Field label="Krótki opis" hint="Opcjonalnie, np. „Umowa utrzymania na 2026”.">
          <Input
            value={description}
            maxLength={FILE_DESCRIPTION_MAX}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Czego dotyczy plik"
          />
        </Field>
      </form>
    </Dialog>
  );
}
